import { createContext, useContext, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { getSnapshotUrl, fetchPreviewData } from "@/lib/cms";
import homePage from "@/data/pages/home";
import aboutPage from "@/data/pages/about";
import howItWorksPage from "@/data/pages/how-it-works";
import orderPage from "@/data/pages/order";
import defaultSettingsData from "@/data/site-settings";
import {
  collectionsData as defaultCollectionsData,
  collectionsList as defaultCollectionsList,
} from "@/data/collections";

const defaultPages = {
  home: homePage,
  about: aboutPage,
  "how-it-works": howItWorksPage,
  order: orderPage,
};

const CmsContext = createContext({ snapshot: null, isPreview: false });

export function CmsProvider({ children }) {
  const [snapshot, setSnapshot] = useState(null);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preview = params.get("preview") === "true";
    setIsPreview(preview);

    if (preview) {
      // Preview mode: fetch drafts directly from Supabase DB
      fetchPreviewData()
        .then((data) => { if (data) setSnapshot(data); })
        .catch(() => {});
    } else {
      // Normal mode: fetch published snapshot
      const url = getSnapshotUrl();
      if (!url) return;
      fetch(url + "?t=" + Date.now())
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => { if (data) setSnapshot(data); })
        .catch(() => {});
    }
  }, []);

  return (
    <CmsContext.Provider value={{ snapshot, isPreview }}>
      {children}
    </CmsContext.Provider>
  );
}

export function useCms() {
  const { snapshot, isPreview } = useContext(CmsContext);

  return {
    isPreview,
    getPage: (slug) => snapshot?.pages?.[slug] || defaultPages[slug],
    getCollectionsList: () => snapshot?.collectionsList || defaultCollectionsList,
    getCollectionsData: () => snapshot?.collections || defaultCollectionsData,
    getCollection: (slug) =>
      (snapshot?.collections || defaultCollectionsData)[slug] || null,
    getSettings: () => snapshot?.settings || defaultSettingsData,
  };
}

/** Helper: find a section by ID inside a page */
export function findSection(page, sectionId) {
  return page?.sections?.find((s) => s.id === sectionId);
}
